import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CreditCard, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * The one thing an unentitled site sees. Prices, labels and the feature list all come from the
 * backend catalogue — nothing about pricing is written in the UI.
 */
export function UpgradeCard({ siteId, offer, compact = false }) {
  const [interval, setInterval] = useState("monthly");
  const [busy, setBusy] = useState(false);
  if (!offer) return null;

  const price = interval === "annual" ? offer.annual : offer.monthly;
  const per = interval === "annual" ? "/year" : "/month";

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/entitlement/checkout`, {
        tier: offer.tier_key,
        interval,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="upgrade-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-display text-lg font-semibold tracking-tight" data-testid="upgrade-tier">
          Upgrade to {offer.label}
        </p>
        <p className="text-sm font-medium" data-testid="upgrade-price">
          ${price}
          <span className="text-muted-foreground">{per}</span>
        </p>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{offer.blurb}</p>

      {!compact && (
        <ul className="mt-3 space-y-1.5">
          {(offer.features || []).map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button onClick={upgrade} disabled={busy} data-testid="upgrade-button">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
          Upgrade — ${price}{per}
        </Button>
        <button
          type="button"
          data-testid="upgrade-interval-toggle"
          onClick={() => setInterval(interval === "monthly" ? "annual" : "monthly")}
          className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          {interval === "monthly" ? "Pay yearly and save two months" : "Switch to monthly"}
        </button>
      </div>
      <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
        <ArrowUpRight className="h-3 w-3" /> Secure checkout on Stripe. Cancel any time through billing.
      </p>
    </div>
  );
}

export function PastDueBanner({ status }) {
  if (status !== "past_due") return null;
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400"
      data-testid="past-due-banner">
      Payment issue — please update your card so this add-on keeps running. Nothing has been switched off yet.
    </div>
  );
}

export function PausedBanner({ entitlement }) {
  if (entitlement?.status !== "paused") return null;
  const until = entitlement.paused_until ? new Date(entitlement.paused_until).toLocaleDateString() : null;
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400"
      data-testid="paused-banner">
      This add-on is paused. Your connected accounts are kept{until ? ` until ${until}` : ""}, but posting and syncing
      are off until billing is sorted out.
    </div>
  );
}
